/* EV Okendo Reviews: the carousel, the product filter and the photo modal.

   The track is a native scroll-snap row, so swiping, trackpads, the scrollbar
   and keyboard focus all work with no script at all. This adds the arrows, the
   position bars, the chip filter and the photo dialog on top, and reads carousel
   state back off scrollLeft rather than keeping its own index, so a user swipe
   and an arrow click can never disagree.

   Paging is by the page, not by the card: the bars mirror the Figma, where 19
   reviews three at a time give seven bars. */

const FOCUSABLE =
  'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])';

class EvOkendoReviews extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-okr-track]');
    if (!this.track) return;

    /* data-static is read by CSS on the section, which carries the colour
       scheme classes, so it is set there rather than on this element. */
    this.root = this.closest('.ev-okr') || this;

    this.items = Array.from(this.track.children);
    if (!this.items.length) return;

    this.dotsHost = this.querySelector('[data-okr-dots]');
    this.emptyNote = this.querySelector('[data-okr-empty]');
    this.chips = Array.from(this.querySelectorAll('[data-okr-filter]'));
    /* The head arrows sit outside this element, beside the title, while the
       foot arrows sit inside it. Both pairs belong to this section, so they are
       queried from the section root rather than from this element; scoping to
       `this` silently left the desktop arrows unbound. */
    this.prevButtons = Array.from(this.root.querySelectorAll('[data-okr-prev]'));
    this.nextButtons = Array.from(this.root.querySelectorAll('[data-okr-next]'));

    this.dots = [];
    this.page = 0;
    this.photoIndex = 0;
    this.photos = [];

    this.sync = this.sync.bind(this);
    this.onScroll = this.onScroll.bind(this);
    this.onKeydown = this.onKeydown.bind(this);

    this.prevButtons.forEach((button) => button.addEventListener('click', () => this.step(-1)));
    this.nextButtons.forEach((button) => button.addEventListener('click', () => this.step(1)));
    this.chips.forEach((chip) =>
      chip.addEventListener('click', () => this.filter(chip.getAttribute('data-okr-filter')))
    );

    this.track.addEventListener('scroll', this.onScroll, { passive: true });

    this.setupModal();
    this.setupCards();

    /* Item widths are percentage based, so a resize changes how many fit and
       therefore the number of pages and whether the controls are needed. */
    if (typeof ResizeObserver !== 'undefined') {
      this.observer = new ResizeObserver(this.sync);
      this.observer.observe(this.track);
    } else {
      window.addEventListener('resize', this.sync);
    }

    this.sync();
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
    window.removeEventListener('resize', this.sync);
    document.removeEventListener('keydown', this.onKeydown);
    if (this.frame) cancelAnimationFrame(this.frame);
    /* The modal lives on <body>, so it has to be taken with us. */
    if (this.modal && this.modal.parentNode) this.modal.parentNode.removeChild(this.modal);
  }

  /* ------------------------------------------------------------- carousel */

  get visibleItems() {
    return this.items.filter((item) => !item.hidden);
  }

  /* One card plus one gap, measured rather than assumed, so the gap and the
     card width can change at any breakpoint without touching this. */
  stride() {
    const visible = this.visibleItems;
    if (visible.length > 1) {
      const delta = visible[1].offsetLeft - visible[0].offsetLeft;
      if (delta > 0) return delta;
    }
    return visible.length ? visible[0].getBoundingClientRect().width : 0;
  }

  perPage() {
    const stride = this.stride();
    if (!stride) return 1;
    return Math.max(1, Math.round(this.track.clientWidth / stride));
  }

  step(direction) {
    const distance = this.stride() * this.perPage();
    if (!distance) return;
    this.track.scrollBy({ left: direction * distance, behavior: 'smooth' });
  }

  goToPage(page) {
    const visible = this.visibleItems;
    if (!visible.length) return;
    const origin = visible[0].offsetLeft;
    const target = visible[page * this.perPage()] || visible[visible.length - 1];
    this.track.scrollTo({ left: target.offsetLeft - origin, behavior: 'smooth' });
  }

  onScroll() {
    if (this.frame) return;
    this.frame = requestAnimationFrame(() => {
      this.frame = null;
      this.updateState();
    });
  }

  /* Cheap enough to run on every scroll frame: reads scroll offsets, writes
     only attributes that were not already set. */
  updateState() {
    const scrollable = this.track.scrollWidth - this.track.clientWidth;

    /* A 1px tolerance: fractional layout widths mean scrollLeft rarely lands
       exactly on either end. */
    this.root.toggleAttribute('data-static', scrollable <= 1);

    const atStart = this.track.scrollLeft <= 1;
    const atEnd = this.track.scrollLeft >= scrollable - 1;

    this.prevButtons.forEach((button) => {
      button.disabled = atStart;
    });
    this.nextButtons.forEach((button) => {
      button.disabled = atEnd;
    });

    const perPage = this.perPage();
    const stride = this.stride();
    const page = stride ? Math.round(this.track.scrollLeft / (stride * perPage)) : 0;
    this.dots.forEach((dot, index) => {
      dot.setAttribute('aria-selected', index === page ? 'true' : 'false');
    });

    /* The bar strip scrolls when there are more pages than fit across it, and
       mobile gives one bar per review, so the active bar has to be pulled into
       view or the indicator stops indicating. Guarded on an actual page change
       because this runs on every scroll frame. */
    if (page !== this.page) {
      this.page = page;
      this.revealDot(page);
    }
  }

  /* Scrolls the bar strip ONLY.

     This used to be `active.scrollIntoView({ block: 'nearest', inline: 'nearest' })`,
     which walks up the tree and scrolls every scrollable ancestor it needs to —
     including the document. On desktop the strip has no overflow at all, so the
     horizontal part was a no-op while the vertical part still yanked the whole
     page down by ~400px on every arrow click. Measured, on the live homepage.

     Doing the arithmetic by hand keeps the effect inside the strip: if the bar
     is already fully visible, nothing moves. */
  revealDot(index) {
    const host = this.dotsHost;
    const dot = this.dots[index];
    if (!host || !dot) return;
    if (host.scrollWidth - host.clientWidth <= 1) return;

    const left = dot.offsetLeft;
    const right = left + dot.offsetWidth;

    if (left < host.scrollLeft) {
      host.scrollLeft = left;
    } else if (right > host.scrollLeft + host.clientWidth) {
      host.scrollLeft = right - host.clientWidth;
    }
  }

  /* The expensive pass. Only the things that can change when the width or the
     set of visible cards changes, so it is kept off the scroll path: rebuilding
     bars and measuring clamped bodies both force layout. */
  sync() {
    this.buildDots();
    this.measureClamps();
    this.updateState();
  }

  buildDots() {
    if (!this.dotsHost) return;

    const pages = Math.max(1, Math.ceil(this.visibleItems.length / this.perPage()));
    if (this.dots.length === pages) return;

    this.dotsHost.textContent = '';
    this.dots = [];
    for (let index = 0; index < pages; index += 1) {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'ev-okr__dot';
      dot.setAttribute('role', 'tab');
      dot.setAttribute('aria-selected', 'false');
      dot.setAttribute('aria-label', `Go to review page ${index + 1} of ${pages}`);
      dot.addEventListener('click', () => this.goToPage(index));
      this.dotsHost.appendChild(dot);
      this.dots.push(dot);
    }
  }

  /* ---------------------------------------------------------------- filter */

  filter(group) {
    this.chips.forEach((chip) => {
      chip.setAttribute('aria-pressed', chip.getAttribute('data-okr-filter') === group ? 'true' : 'false');
    });

    this.items.forEach((item) => {
      item.hidden = group !== '' && item.getAttribute('data-okr-group') !== group;
    });

    const count = this.visibleItems.length;
    if (this.emptyNote) this.emptyNote.hidden = count > 0;

    /* Jump rather than glide: the track's contents have changed underneath, so
       an animated scroll would just be a smear. The track sets
       `scroll-behavior: smooth`, which a plain scrollLeft assignment would
       obey, hence the explicit 'instant'. */
    this.track.scrollTo({ left: 0, behavior: 'instant' });
    this.sync();
  }

  /* ------------------------------------------------------------ read more */

  setupCards() {
    this.items.forEach((item) => {
      const more = item.querySelector('[data-okr-more]');
      if (more) more.addEventListener('click', () => this.open(item, 0));

      item.querySelectorAll('[data-okr-photo]').forEach((photo) => {
        photo.addEventListener('click', () => {
          this.open(item, Number(photo.getAttribute('data-okr-photo-index')) || 0);
        });
      });
    });
  }

  /* The "Read full review" control only earns its place when the clamp is
     actually cutting text off, which cannot be known until the card has been
     laid out at this width. */
  measureClamps() {
    this.items.forEach((item) => {
      const body = item.querySelector('[data-okr-body]');
      const more = item.querySelector('[data-okr-more]');
      if (!body || !more) return;
      more.hidden = body.scrollHeight <= body.clientHeight + 1;
    });
  }

  /* ----------------------------------------------------------------- modal */

  setupModal() {
    this.modal = this.querySelector('[data-okr-modal]');
    if (!this.modal) return;

    /* Re-parented to <body> so `position: fixed` is measured against the
       viewport whatever the section is sitting inside. */
    document.body.appendChild(this.modal);

    this.dialog = this.modal.querySelector('.ev-okr__dialog');
    this.modalMedia = this.modal.querySelector('[data-okr-modal-media]');
    this.modalStage = this.modal.querySelector('[data-okr-modal-stage]');
    this.modalThumbs = this.modal.querySelector('[data-okr-modal-thumbs]');
    this.modalDetail = this.modal.querySelector('[data-okr-modal-detail]');
    this.modalCount = this.modal.querySelector('[data-okr-modal-count]');
    this.photoPrev = this.modal.querySelector('[data-okr-photo-prev]');
    this.photoNext = this.modal.querySelector('[data-okr-photo-next]');

    this.modal.querySelectorAll('[data-okr-close]').forEach((button) => {
      button.addEventListener('click', () => this.close());
    });
    if (this.photoPrev) this.photoPrev.addEventListener('click', () => this.showPhoto(this.photoIndex - 1));
    if (this.photoNext) this.photoNext.addEventListener('click', () => this.showPhoto(this.photoIndex + 1));
  }

  open(item, photoIndex) {
    if (!this.modal) return;

    const detail = item.querySelector('template[data-okr-detail]');
    const photos = item.querySelector('template[data-okr-photos]');

    this.modalDetail.textContent = '';
    if (detail) this.modalDetail.appendChild(detail.content.cloneNode(true));

    this.photos = photos ? Array.from(photos.content.cloneNode(true).querySelectorAll('img')) : [];
    this.buildThumbs();

    const hasPhotos = this.photos.length > 0;
    this.modalMedia.hidden = !hasPhotos;
    /* With no photo panel the dialog would otherwise run the full 1280 and set
       the review as one very long measure. */
    this.dialog.classList.toggle('ev-okr__dialog--text-only', !hasPhotos);
    if (hasPhotos) this.showPhoto(photoIndex);

    this.opener = document.activeElement;
    this.modal.hidden = false;
    document.body.classList.add('ev-okr-modal-open');
    document.addEventListener('keydown', this.onKeydown);

    /* The scrim also carries data-okr-close and comes first in the DOM, so the
       button has to be asked for by name or focus goes nowhere. */
    const close = this.modal.querySelector('button[data-okr-close]');
    if (close) close.focus();
  }

  close() {
    if (!this.modal || this.modal.hidden) return;
    this.modal.hidden = true;
    document.body.classList.remove('ev-okr-modal-open');
    document.removeEventListener('keydown', this.onKeydown);
    if (this.opener && this.opener.focus) this.opener.focus();
  }

  buildThumbs() {
    if (!this.modalThumbs) return;
    this.modalThumbs.textContent = '';
    this.thumbButtons = [];

    /* A single photo needs no strip and no arrows. */
    if (this.photos.length < 2) return;

    this.photos.forEach((photo, index) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'ev-okr__modal-thumb';
      button.setAttribute('aria-label', `Show photo ${index + 1} of ${this.photos.length}`);
      button.setAttribute('aria-current', 'false');

      const image = photo.cloneNode(true);
      image.removeAttribute('class');
      image.setAttribute('sizes', '72px');
      image.setAttribute('loading', 'lazy');
      button.appendChild(image);

      button.addEventListener('click', () => this.showPhoto(index));
      this.modalThumbs.appendChild(button);
      this.thumbButtons.push(button);
    });
  }

  showPhoto(index) {
    if (!this.photos.length) return;

    const total = this.photos.length;
    this.photoIndex = (index + total) % total;

    this.modalStage.querySelectorAll('img').forEach((image) => image.remove());
    /* Inserted first so it paints behind the arrows and the counter, which are
       absolutely positioned siblings. */
    this.modalStage.insertBefore(this.photos[this.photoIndex].cloneNode(true), this.modalStage.firstChild);

    if (this.modalCount) this.modalCount.textContent = total > 1 ? `${this.photoIndex + 1} / ${total}` : '';
    if (this.photoPrev) this.photoPrev.hidden = total < 2;
    if (this.photoNext) this.photoNext.hidden = total < 2;

    if (this.thumbButtons) {
      this.thumbButtons.forEach((button, position) => {
        button.setAttribute('aria-current', position === this.photoIndex ? 'true' : 'false');
      });
    }
  }

  onKeydown(event) {
    if (!this.modal || this.modal.hidden) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }

    if (event.key === 'ArrowLeft' && this.photos.length > 1) {
      this.showPhoto(this.photoIndex - 1);
      return;
    }

    if (event.key === 'ArrowRight' && this.photos.length > 1) {
      this.showPhoto(this.photoIndex + 1);
      return;
    }

    /* Keep Tab inside the dialog: an open modal that lets focus wander behind
       the scrim is worse than no modal at all for a keyboard user. */
    if (event.key !== 'Tab') return;

    const focusable = Array.from(this.dialog.querySelectorAll(FOCUSABLE)).filter(
      (node) => !node.hidden && node.offsetParent !== null
    );
    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }
}

if (!customElements.get('ev-okendo-reviews')) {
  customElements.define('ev-okendo-reviews', EvOkendoReviews);
}